/**
 * Google Maps Configuration Service
 * Provides Google Maps API configuration for shared use across the application
 * This service abstracts the API key retrieval into a common layer,
 * eliminating cross-domain dependencies
 */

import { LoggerService } from './LoggerService';

const logger = LoggerService.create('GoogleMapsConfigService');

export class GoogleMapsConfigService {
  private static instance: GoogleMapsConfigService;
  private apiKey: string | null = null;

  private constructor() {
    this.initializeApiKey();
  }

  /**
   * Get singleton instance of GoogleMapsConfigService
   */
  public static getInstance(): GoogleMapsConfigService {
    if (!GoogleMapsConfigService.instance) {
      GoogleMapsConfigService.instance = new GoogleMapsConfigService();
    }
    return GoogleMapsConfigService.instance;
  }

  /**
   * Initialize API key from environment variables
   */
  private initializeApiKey(): void {
    this.apiKey = import.meta.env.VITE_GOOGLE_API_KEY || null;
  }

  /**
   * Get the Google Maps API key
   * @throws Error if API key is not configured
   */
  public getApiKey(): string {
    if (!this.apiKey) {
      const errorMessage = 'Google Maps API key not found. Please set VITE_GOOGLE_API_KEY environment variable.';
      logger.error(errorMessage);
      throw new Error(errorMessage);
    }
    return this.apiKey;
  }

  /**
   * Check if Google Maps API is properly configured
   */
  public isConfigured(): boolean {
    return !!this.apiKey;
  }
}
